import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaFile, FaFloppyDisk, FaCheck, FaTriangleExclamation, FaCircleInfo } from 'react-icons/fa6';
import { SideDrawer } from '../../../../shared/components/overlays/SideDrawer';
import { CSVFileEntry } from '../../../../types/common';
import { ColorTheme } from './CSVFileHeader';
import {
  MetadataSection,
  ConfigSection,
  ScalingSection,
} from './sections';

interface ValidationError {
  field: string;
  message: string;
}

interface ExpandedSections {
  metadata: boolean;
  config: boolean;
  adjustment: boolean;
  scaling: boolean;
}

interface CSVFileSettingsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  entry: CSVFileEntry;
  onInputChange: (field: keyof CSVFileEntry, value: string) => void;
  onRadioChange: (value: 'ja' | 'nein', field: 'skalierung' | 'mittelwertbildungÜberDenZeithorizont') => void;
  colorTheme?: ColorTheme;
  isDirty?: boolean;
  onSave?: () => void;
  /** Whether the file has been uploaded to storage (has a storage path) */
  hasStoragePath?: boolean;
}

/**
 * Settings drawer for CSV file entry
 * Contains all 4 configuration sections in a slide-out panel
 */
export const CSVFileSettingsDrawer: React.FC<CSVFileSettingsDrawerProps> = ({
  isOpen,
  onClose,
  entry,
  onInputChange,
  onRadioChange,
  colorTheme = 'blue',
  isDirty = false,
  onSave,
  hasStoragePath = false,
}) => {
  const { t } = useTranslation('training');

  // Local state for expanded sections within drawer
  const [expandedSections, setExpandedSections] = useState<ExpandedSections>({
    metadata: true,
    config: false,
    adjustment: false,
    scaling: false,
  });

  const handleToggleSection = (section: keyof ExpandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Validation logic - check all required fields
  const validationErrors = useMemo<ValidationError[]>(() => {
    const errors: ValidationError[] = [];

    // Config section validation — output files have no dropdown (always Historische Daten)
    if (!entry.datenform && colorTheme !== 'green') {
      errors.push({ field: 'datenform', message: t('csvFileUploader.validation.required', 'Required') });
    }

    // Zeithorizont required when datenform is "Historische Daten"
    if (entry.datenform === 'Historische Daten') {
      if (!entry.zeithorizontStart || entry.zeithorizontStart === '') {
        errors.push({ field: 'zeithorizontStart', message: t('csvFileUploader.validation.required', 'Required') });
      }
      if (!entry.zeithorizontEnd || entry.zeithorizontEnd === '') {
        errors.push({ field: 'zeithorizontEnd', message: t('csvFileUploader.validation.required', 'Required') });
      }
    }

    // Adjustment section validation — skipped while section is hidden (methods not implemented yet)

    // Scaling section validation - min/max required when skalierung is 'ja'
    if (entry.skalierung === 'ja') {
      if (!entry.skalierungMin || entry.skalierungMin === '') {
        errors.push({ field: 'skalierungMin', message: t('csvFileUploader.validation.required', 'Required') });
      }
      if (!entry.skalierungMax || entry.skalierungMax === '') {
        errors.push({ field: 'skalierungMax', message: t('csvFileUploader.validation.required', 'Required') });
      }
      // Also check min <= max
      const minVal = parseFloat(entry.skalierungMin || '0');
      const maxVal = parseFloat(entry.skalierungMax || '0');
      if (!isNaN(minVal) && !isNaN(maxVal) && minVal > maxVal) {
        errors.push({ field: 'skalierungMin', message: t('csvFileUploader.validation.minGreaterThanMax', 'Min cannot be greater than Max') });
      }
    }

    return errors;
  }, [entry, t]);

  const isValid = validationErrors.length === 0;

  const themeLabel = colorTheme === 'blue'
    ? t('csvFileUploader.drawer.inputSettings', 'Input File Settings')
    : t('csvFileUploader.drawer.outputSettings', 'Output File Settings');

  const fileInfoBadgeClass = colorTheme === 'blue'
    ? 'bg-accent/50 border border-primary/20'
    : 'bg-emerald-50/50 border border-emerald-200/40';
  const fileIconClass = colorTheme === 'blue' ? 'text-primary' : 'text-emerald-500';

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={entry.fileName || t('csvFileUploader.drawer.noFile', 'No file selected')}
      subtitle={themeLabel}
      size="lg"
      position="right"
    >
      <div className="p-4 space-y-3">
        {/* File info badge */}
        <div className={`flex items-center gap-3 p-3 rounded-lg ${fileInfoBadgeClass}`}>
          <FaFile className={`${fileIconClass} text-sm flex-shrink-0`} />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-foreground truncate">
              {entry.bezeichnung || t('csvFileUploader.drawer.noBezeichnung', 'No designation')}
            </p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {entry.datenpunkte
                ? t('csvFileUploader.drawer.dataPoints', '{{count}} data points', { count: parseInt(entry.datenpunkte, 10) || 0 })
                : t('csvFileUploader.drawer.noData', 'No data')
              }
            </p>
          </div>
        </div>

        {/* Section 1: Metadata */}
        <MetadataSection
          entry={entry}
          isExpanded={expandedSections.metadata}
          onToggle={() => handleToggleSection('metadata')}
        />

        {/* Section 2: Config */}
        <ConfigSection
          entry={entry}
          isExpanded={expandedSections.config}
          onToggle={() => handleToggleSection('config')}
          onInputChange={onInputChange}
          onRadioChange={(value) => onRadioChange(value, 'mittelwertbildungÜberDenZeithorizont')}
          isOutputData={colorTheme === 'green'}
        />

        {/* Section 3: Adjustment — hidden until Mittelwertbildung and Nächster Wert are implemented */}

        {/* Section 4: Scaling */}
        <ScalingSection
          entry={entry}
          isExpanded={expandedSections.scaling}
          onToggle={() => handleToggleSection('scaling')}
          onInputChange={onInputChange}
          onRadioChange={(value) => onRadioChange(value, 'skalierung')}
        />
      </div>

      {/* Save status footer */}
      <div className={`sticky bottom-0 px-4 py-3 border-t ${
        !isValid ? 'bg-red-50/80 border-red-200/60' :
        isDirty ? 'bg-amber-50/80 border-amber-200/60' :
        hasStoragePath ? 'bg-emerald-50/80 border-emerald-200/60' : 'bg-accent/80 border-primary/20'
      }`}>
        {/* Validation errors */}
        {!isValid && (
          <div className="mb-3 p-3 bg-red-100/60 rounded-lg">
            <div className="flex items-center gap-1.5 text-red-700 text-xs font-medium mb-1.5">
              <FaTriangleExclamation className="w-3 h-3" />
              {t('csvFileUploader.drawer.requiredFields', 'Required fields missing:')}
            </div>
            <ul className="text-red-600 text-xs space-y-0.5 pl-4 list-disc">
              {validationErrors.map((error, idx) => (
                <li key={idx}>{t(`csvFileUploader.headers.${error.field}`, error.field)}</li>
              ))}
            </ul>
          </div>
        )}

        {isDirty && onSave ? (
          <button
            type="button"
            onClick={onSave}
            disabled={!isValid}
            className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${
              isValid
                ? 'bg-amber-500 hover:bg-amber-600 text-white active:scale-[0.98]'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            <FaFloppyDisk className="w-3.5 h-3.5" />
            {t('csvFileUploader.drawer.saveChanges', 'Save Changes')}
          </button>
        ) : isValid ? (
          hasStoragePath ? (
            <div className="flex items-center justify-center gap-2 text-emerald-600 text-xs font-medium">
              <FaCheck className="w-3 h-3" />
              {t('csvFileUploader.drawer.allSaved', 'All changes saved')}
            </div>
          ) : (
            <div className="flex items-center justify-center gap-2 text-primary text-xs font-medium">
              <FaCircleInfo className="w-3 h-3" />
              {t('csvFileUploader.drawer.savedWithUpload', 'Will be saved on upload')}
            </div>
          )
        ) : null}
      </div>
    </SideDrawer>
  );
};

export default CSVFileSettingsDrawer;
